/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getSelectedProductIdFromActionInput } from '../utilities/utils';
import { Product } from '@msdyn365-commerce/retail-proxy';
import { searchAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

/**
 * GetProductSearch Input Action
 */
export class GetProductSearchInput implements Msdyn365.IActionInput {
    public productId: number;

    public constructor(productId: number) {
        this.productId = productId;
    }

    public getCacheKey = () => `ProductSearch`;
    public getCacheObjectType = () => 'Product';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

/**
 * CreateInput method for the GetProductSearch data action.
 * @param inputData - The input passed into the createInput method.
 * @returns The input for the data action getProductSearch
 */
const createInput = (inputData: Msdyn365.ICreateActionContext): GetProductSearchInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new GetProductSearchInput(+productId);
    }

    throw new Error('Unable to create GetProductSearchInput, no productId found on module config or query');
};

/**
 * Runs a search for a product based on the inputs product id
 * @param input - The GetProductSearchInput containing the product id to search
 * @param ctx  - Context of the data action
 * @returns - The product if found, otherwise null
 */
async function action(input: GetProductSearchInput, ctx: Msdyn365.IActionContext): Promise<Product> {
    const products = await searchAsync({ callerContext: ctx }, { Ids: [input.productId] });
    return products[0] ?? null;
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<Product>>action,
    id: 'GetProductSearch',
    input: createInput
});
